import React, { useEffect, useState, useCallback } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { platformApiFetch } from './platformApi';
import { usePlatformAuth } from '../context/PlatformAuthContext';

const INVOICE_STATUSES = ['draft', 'issued', 'paid', 'overdue', 'void'];

function toDatetimeLocalValue(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromDatetimeLocalValue(s) {
  if (!s || !String(s).trim()) return null;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

export default function PlatformBillingInvoiceFormPage() {
  const { invoiceId } = useParams();
  const isEdit = Boolean(invoiceId);
  const navigate = useNavigate();
  const { user, ready } = usePlatformAuth();
  const isSuper = user?.role === 'super_admin';

  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState('');
  const [reference, setReference] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [period, setPeriod] = useState('');
  const [invStatus, setInvStatus] = useState('issued');
  const [issuedAt, setIssuedAt] = useState('');
  const [dueAt, setDueAt] = useState('');
  const [paidAt, setPaidAt] = useState('');
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await platformApiFetch('/api/platform/tenants?pageSize=500&sort=tenant_name&order=asc');
        const json = await res.json().catch(() => ({}));
        if (res.ok && !cancelled) setTenants(json.tenants || []);
      } catch (_e) {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(async () => {
    if (!isEdit) return;
    setLoading(true);
    setError(null);
    try {
      const res = await platformApiFetch(`/api/platform/billing/invoices/${invoiceId}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Failed to load');
      const inv = json.invoice;
      setTenantId(String(inv.tenant_id));
      setReference(inv.invoice_reference || '');
      setAmount(inv.amount != null ? String(inv.amount) : '');
      setCurrency(inv.currency || 'USD');
      setPeriod(inv.billing_period || '');
      setInvStatus(inv.invoice_status || 'issued');
      setIssuedAt(toDatetimeLocalValue(inv.issued_at));
      setDueAt(toDatetimeLocalValue(inv.due_at));
      setPaidAt(toDatetimeLocalValue(inv.paid_at));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [isEdit, invoiceId]);

  useEffect(() => {
    if (isEdit) load();
  }, [load, isEdit]);

  if (!ready) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isSuper) {
    return <Navigate to={isEdit ? `/platform/billing/invoices/${invoiceId}` : '/platform/billing/invoices'} replace />;
  }

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        const body = {
          invoice_reference: reference.trim(),
          amount: Number(amount),
          currency: (currency || 'USD').trim().toUpperCase().slice(0, 3),
          billing_period: period.trim() || null,
          invoice_status: invStatus,
          issued_at: fromDatetimeLocalValue(issuedAt),
          due_at: fromDatetimeLocalValue(dueAt),
          paid_at: fromDatetimeLocalValue(paidAt)
        };
        const res = await platformApiFetch(`/api/platform/billing/invoices/${invoiceId}`, {
          method: 'PATCH',
          body
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json.error || 'Save failed');
        navigate(`/platform/billing/invoices/${invoiceId}`);
      } else {
        const tid = Number(tenantId);
        if (!Number.isFinite(tid)) {
          setError('Select a tenant');
          setSaving(false);
          return;
        }
        const res = await platformApiFetch('/api/platform/billing/invoices', {
          method: 'POST',
          body: {
            tenant_id: tid,
            invoice_reference: reference.trim(),
            amount: Number(amount),
            currency: (currency || 'USD').trim().toUpperCase().slice(0, 3),
            billing_period: period.trim() || null,
            invoice_status: invStatus,
            issued_at: fromDatetimeLocalValue(issuedAt),
            due_at: fromDatetimeLocalValue(dueAt),
            paid_at: fromDatetimeLocalValue(paidAt)
          }
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json.error || 'Create failed');
        navigate(`/platform/billing/invoices/${json.invoice.id}`);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5">{isEdit ? 'Edit invoice' : 'New invoice'}</Typography>
        <Button variant="outlined" component={Link} to={isEdit ? `/platform/billing/invoices/${invoiceId}` : '/platform/billing/invoices'}>
          Cancel
        </Button>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Paper sx={{ p: 2, maxWidth: 720 }}>
        <Box component="form" onSubmit={save}>
          {!isEdit ? (
            <FormControl fullWidth margin="normal" required>
              <InputLabel>Tenant</InputLabel>
              <Select label="Tenant" value={tenantId} onChange={(e) => setTenantId(e.target.value)}>
                <MenuItem value="">
                  <em>Select tenant</em>
                </MenuItem>
                {tenants.map((t) => (
                  <MenuItem key={t.id} value={String(t.id)}>
                    {t.tenant_name} (#{t.id})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <TextField fullWidth margin="normal" label="Tenant ID" value={tenantId} disabled />
          )}
          <TextField
            fullWidth
            required
            label="Invoice reference"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            margin="normal"
          />
          <TextField
            fullWidth
            required
            label="Amount"
            type="number"
            inputProps={{ min: 0, step: '0.01' }}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Currency (ISO 4217)"
            value={currency}
            onChange={(e) => setCurrency(e.target.value.toUpperCase().slice(0, 3))}
            margin="normal"
            inputProps={{ maxLength: 3 }}
          />
          <TextField fullWidth label="Billing period" value={period} onChange={(e) => setPeriod(e.target.value)} margin="normal" />
          <FormControl fullWidth margin="normal">
            <InputLabel>Invoice status</InputLabel>
            <Select label="Invoice status" value={invStatus} onChange={(e) => setInvStatus(e.target.value)}>
              {INVOICE_STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Issued at"
            type="datetime-local"
            value={issuedAt}
            onChange={(e) => setIssuedAt(e.target.value)}
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            fullWidth
            label="Due at"
            type="datetime-local"
            value={dueAt}
            onChange={(e) => setDueAt(e.target.value)}
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            fullWidth
            label="Paid at"
            type="datetime-local"
            value={paidAt}
            onChange={(e) => setPaidAt(e.target.value)}
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
          <Box sx={{ mt: 2 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={saving || !reference.trim() || amount === '' || Number.isNaN(Number(amount))}
            >
              {saving ? 'Saving…' : isEdit ? 'Save' : 'Create'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
